import ApiService from "@/services/api.service";

// ACTIONS
export const FETCH_PERIODI_FATTURAZIONE = "fetchPeriodiFatturazione";
export const FETCH_RIGHE_FATTURAZIONE = "fetchRigheFatturazione";
export const RICALCOLA_PERIODO = "ricalcolaPeriodo";
export const INVIA_ORA = "inviaOra";
export const FETCH_INVII = "fetchInvii";
export const ESPORTA_EXCEL_MENSILE = "esportaExcelMensile";

// MUTATIONS
export const SET_PERIODI = "setPeriodi";
export const SET_RIGHE = "setRighe";
export const SET_INVII = "setInvii";
export const SET_LOADING = "setLoading";
export const SET_LOADING_OPERAZIONE = "setLoadingOperazione";
export const SET_ERROR = "setError";
export const SET_ERRORE_CARICAMENTO = "setErroreCaricamento";
export const SET_ERRORE_INVII = "setErroreInvii";

// Controller di backend: [Route("api/[controller]")]
//  - /api/FatturazioneRighe          -> periodi elaborati e righe del periodo
//  - /api/Fatturazione/RicalcolaMensile -> ricalcolo senza invio né salvataggio
//  - /api/Fatturazione/InviaOra      -> invio manuale (genera, carica, invia email)
//  - /api/Fatturazione/Invii         -> storico degli invii
//  - /api/Fatturazione/ExcelMensile  -> download del file (senza email)
// La baseURL di axios è VUE_APP_ROOT_API (default "/api").
const RESOURCE_RIGHE = "FatturazioneRighe";
const RESOURCE_FATTURAZIONE = "Fatturazione";

// Testo usato quando l'errore non contiene un messaggio leggibile.
const MESSAGGIO_ERRORE_GENERICO =
    "Si è verificato un errore, riprova più tardi.";

// Il backend risponde agli errori con HTTP 500 e body {Status, Message}
// (Response<object> di questa codebase). A seconda della configurazione JSON il
// campo arriva come "message" o "Message": si accettano entrambe le forme, più le
// varianti ProblemDetails, e in ogni caso la mutation riceve una stringa.
const toMessage = (err) => {
    if (typeof err === "string") {
        return err.trim() || MESSAGGIO_ERRORE_GENERICO;
    }
    if (!err) {
        return MESSAGGIO_ERRORE_GENERICO;
    }

    const candidati = [
        err.response?.data?.message,
        err.response?.data?.Message,
        err.response?.data?.detail,
        err.response?.data?.title,
        err.response?.data,
        err.data?.message,
        err.data?.Message,
        err.detail,
        err.title,
        err.message,
    ];
    const messaggio = candidati.find(
        (valore) => typeof valore === "string" && valore.trim() !== ""
    );
    if (messaggio) {
        return messaggio.trim();
    }

    try {
        const serializzato = JSON.stringify(err);
        if (serializzato && serializzato !== "{}" && serializzato !== "[]") {
            return serializzato;
        }
    } catch {
        // Oggetto non serializzabile: resta il messaggio generico.
    }

    return MESSAGGIO_ERRORE_GENERICO;
};

// Il ricalcolo e l'invio rispondono 200 con un messaggio. A seconda del
// content-type può arrivare come testo semplice, come stringa JSON oppure come
// oggetto { "message": "..." }; qui viene riportato alla forma testuale, senza
// inventare contenuto.
const toStringa = (data) => {
    if (typeof data === "string") {
        const senzaVirgolette = data.replace(/^"(.*)"$/s, "$1").trim();
        // Body JSON serializzato ({ "message": "..." }) servito come testo.
        if (senzaVirgolette.startsWith("{")) {
            try {
                return toStringa(JSON.parse(senzaVirgolette));
            } catch {
                return senzaVirgolette;
            }
        }
        return senzaVirgolette;
    }
    if (data === null || data === undefined) {
        return "";
    }
    if (typeof data === "object") {
        const messaggio = data.message || data.Message;
        if (typeof messaggio === "string") return messaggio.trim();
    }
    return String(data);
};

// Nome del file allegato dal backend: se l'header non è leggibile (es. CORS
// senza Access-Control-Expose-Headers) si ricade sul nome convenzionale del
// report, con il mese a due cifre.
const nomeFileDaHeader = (headers, anno, mese) => {
    const contentDisposition =
        headers && (headers["content-disposition"] || headers["Content-Disposition"]);
    if (typeof contentDisposition === "string" && contentDisposition !== "") {
        const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(
            contentDisposition
        );
        if (match && match[1]) {
            const nome = match[1].replace(/['"]/g, "").trim();
            if (nome !== "") return nome;
        }
    }
    const mm = String(mese).padStart(2, "0");
    return `Fatturazione_${anno}_${mm}.xlsx`;
};

const state = {
    // {year, month, numeroRighe, calcolatoIlMax}[] dal più recente.
    periodi: [],
    // Righe del periodo selezionato (tutti i clienti: vista dell'operatore).
    righe: [],
    // Storico degli invii del periodo selezionato, dal più recente:
    // {id, year, month, tipo, inviatoil, destinatari, nomefile, esito,
    //  messaggioerrore, operatore}.
    invii: [],
    loading: false,
    // Operazione in corso (ricalcolo, invio manuale oppure esportazione Excel).
    loadingOperazione: false,
    error: {},
    // Errore del caricamento di periodi o righe: stringa (messaggio) oppure null.
    erroreCaricamento: null,
    // Errore del caricamento dello storico invii: stringa oppure null.
    erroreInvii: null,
};

const getters = {
    getPeriodi: (state) => state.periodi,
    getRighe: (state) => state.righe,
    getInvii: (state) => state.invii,
    getLoading: (state) => state.loading,
    getLoadingOperazione: (state) => state.loadingOperazione,
    getError: (state) => {
        if (typeof state.error === "string") {
            return state.error;
        }
        if (!state.error || !Object.keys(state.error).length) {
            return null;
        }
        return toMessage(state.error);
    },
    getErroreCaricamento: (state) => state.erroreCaricamento,
    getErroreInvii: (state) => state.erroreInvii,
};

const actions = {
    // Periodi elaborati. Endpoint: GET /api/FatturazioneRighe/periodi
    // Per un superadmin il backend restituisce tutti i periodi presenti nello
    // snapshot, ordinati dal più recente.
    [FETCH_PERIODI_FATTURAZIONE](context) {
        context.commit(SET_LOADING, true);
        context.commit(SET_ERRORE_CARICAMENTO, null);

        return new Promise((resolve) => {
            ApiService.get(`${RESOURCE_RIGHE}/periodi`)
                .then(({ data }) => {
                    context.commit(SET_PERIODI, data);
                    context.commit(SET_ERRORE_CARICAMENTO, null);
                    resolve(data);
                })
                .catch((error) => {
                    // Svuota l'elenco committato: senza questo la tabella
                    // mostrerebbe le righe del caricamento precedente mentre
                    // l'alert dice che il caricamento è fallito.
                    context.commit(SET_PERIODI, []);
                    const messaggio = toMessage(error);
                    context.commit(SET_ERROR, messaggio);
                    context.commit(SET_ERRORE_CARICAMENTO, messaggio);
                    setTimeout(() => context.commit(SET_ERROR, ""), 5000);
                    resolve([]);
                })
                .finally(() => context.commit(SET_LOADING, false));
        });
    },

    // Righe del periodo, tutti i clienti. Endpoint:
    // GET /api/FatturazioneRighe?anno={anno}&mese={mese}
    // Qui idCliente NON viene passato: è la vista complessiva dell'operatore.
    [FETCH_RIGHE_FATTURAZIONE](context, { anno, mese }) {
        context.commit(SET_LOADING, true);
        context.commit(SET_ERRORE_CARICAMENTO, null);

        const parametri = [
            `anno=${encodeURIComponent(anno)}`,
            `mese=${encodeURIComponent(mese)}`,
        ];

        return new Promise((resolve) => {
            ApiService.get(`${RESOURCE_RIGHE}?${parametri.join("&")}`)
                .then(({ data }) => {
                    context.commit(SET_RIGHE, data);
                    context.commit(SET_ERRORE_CARICAMENTO, null);
                    resolve(data);
                })
                .catch((error) => {
                    context.commit(SET_RIGHE, []);
                    const messaggio = toMessage(error);
                    context.commit(SET_ERROR, messaggio);
                    context.commit(SET_ERRORE_CARICAMENTO, messaggio);
                    setTimeout(() => context.commit(SET_ERROR, ""), 5000);
                    resolve([]);
                })
                .finally(() => context.commit(SET_LOADING, false));
        });
    },

    // Ricalcolo del periodo e riscrittura della fotografia. Endpoint:
    // GET /api/Fatturazione/RicalcolaMensile?year={anno}&month={mese}
    // Endpoint di SOLO ricalcolo: NON invia email e NON salva file sul file
    // server. Il vecchio SalvaReportMensileConRicalcolo non è più usato da
    // questa pagina (faceva entrambe le cose).
    [RICALCOLA_PERIODO](context, { anno, mese }) {
        context.commit(SET_LOADING_OPERAZIONE, true);
        context.commit(SET_ERROR, {});

        const parametri = [
            `year=${encodeURIComponent(anno)}`,
            `month=${encodeURIComponent(mese)}`,
        ];

        return new Promise((resolve, reject) => {
            ApiService.get(
                `${RESOURCE_FATTURAZIONE}/RicalcolaMensile?${parametri.join("&")}`
            )
                .then(({ data }) => {
                    resolve(toStringa(data));
                })
                .catch((error) => {
                    context.commit(SET_ERROR, toMessage(error));
                    setTimeout(() => context.commit(SET_ERROR, ""), 5000);
                    reject(error);
                })
                .finally(() =>
                    context.commit(SET_LOADING_OPERAZIONE, false)
                );
        });
    },

    // Invio manuale del report. Endpoint:
    // POST /api/Fatturazione/InviaOra?year={anno}&month={mese}
    // Effetti REALI: genera il file, lo carica sul file server e invia la email
    // ai referenti della fatturazione. Il backend rifiuta (500 con
    // {Status, Message}) quando per il periodo esiste già un invio MANUALE
    // riuscito; in quel caso il messaggio del backend viene propagato così com'è.
    [INVIA_ORA](context, { anno, mese }) {
        context.commit(SET_LOADING_OPERAZIONE, true);
        context.commit(SET_ERROR, {});

        const parametri = [
            `year=${encodeURIComponent(anno)}`,
            `month=${encodeURIComponent(mese)}`,
        ];

        return new Promise((resolve, reject) => {
            ApiService.post(
                `${RESOURCE_FATTURAZIONE}/InviaOra?${parametri.join("&")}`
            )
                .then(({ data }) => {
                    resolve(toStringa(data));
                })
                .catch((error) => {
                    const messaggio = toMessage(error);
                    context.commit(SET_ERROR, messaggio);
                    setTimeout(() => context.commit(SET_ERROR, ""), 5000);
                    // Il motivo del rifiuto torna al chiamante così com'è.
                    reject(new Error(messaggio));
                })
                .finally(() =>
                    context.commit(SET_LOADING_OPERAZIONE, false)
                );
        });
    },

    // Storico degli invii. Endpoint:
    // GET /api/Fatturazione/Invii?anno={anno}&mese={mese}
    // I due parametri sono opzionali: qui vengono passati solo se valorizzati.
    // La lista arriva già ordinata dal più recente.
    [FETCH_INVII](context, { anno, mese } = {}) {
        context.commit(SET_ERRORE_INVII, null);

        const parametri = [];
        if (anno !== null && anno !== undefined && anno !== "") {
            parametri.push(`anno=${encodeURIComponent(anno)}`);
        }
        if (mese !== null && mese !== undefined && mese !== "") {
            parametri.push(`mese=${encodeURIComponent(mese)}`);
        }
        const query = parametri.length ? `?${parametri.join("&")}` : "";

        return new Promise((resolve) => {
            ApiService.get(`${RESOURCE_FATTURAZIONE}/Invii${query}`)
                .then(({ data }) => {
                    context.commit(SET_INVII, data);
                    context.commit(SET_ERRORE_INVII, null);
                    resolve(Array.isArray(data) ? data : []);
                })
                .catch((error) => {
                    // Svuota l'elenco: senza questo la tabella mostrerebbe gli
                    // invii del caricamento precedente.
                    context.commit(SET_INVII, []);
                    context.commit(SET_ERRORE_INVII, toMessage(error));
                    resolve([]);
                });
        });
    },

    // Excel mensile. Endpoint:
    // GET /api/Fatturazione/ExcelMensile?year={anno}&month={mese}
    // Restituisce il file Fatturazione_<anno>_<mm>.xlsx e NON invia email
    // (l'endpoint che mandava la mail ai referenti non è usato da questa
    // pagina). Il download riprende l'helper di ApiService usato dalle altre
    // esportazioni della codebase.
    [ESPORTA_EXCEL_MENSILE](context, { anno, mese }) {
        context.commit(SET_LOADING_OPERAZIONE, true);
        context.commit(SET_ERROR, {});

        const apiUrl = `${RESOURCE_FATTURAZIONE}/ExcelMensile?year=${encodeURIComponent(
            anno
        )}&month=${encodeURIComponent(mese)}`;

        return new Promise((resolve, reject) => {
            ApiService.downloadFile(apiUrl)
                .then((response) => {
                    const filename = nomeFileDaHeader(
                        response.headers,
                        anno,
                        mese
                    );
                    const blobUrl = window.URL.createObjectURL(
                        new Blob([response.data])
                    );
                    const link = document.createElement("a");
                    link.href = blobUrl;
                    link.setAttribute("download", filename);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(blobUrl);
                    resolve(filename);
                })
                .catch((error) => {
                    // Con responseType "blob" il body dell'errore è un Blob:
                    // il messaggio JSON del backend viene riletto come testo,
                    // altrimenti resta l'errore generico.
                    const dati = error?.response?.data;
                    if (dati instanceof Blob) {
                        dati.text()
                            .then((testo) => {
                                let messaggio = testo;
                                try {
                                    const json = JSON.parse(testo);
                                    messaggio =
                                        json.message ||
                                        json.Message ||
                                        json.detail ||
                                        json.title ||
                                        testo;
                                } catch {
                                    // Body non JSON: resta il testo così com'è.
                                }
                                context.commit(
                                    SET_ERROR,
                                    toMessage(messaggio)
                                );
                            })
                            .catch(() => {
                                context.commit(
                                    SET_ERROR,
                                    toMessage(error)
                                );
                            });
                    } else {
                        context.commit(SET_ERROR, toMessage(error));
                    }
                    setTimeout(() => context.commit(SET_ERROR, ""), 5000);
                    reject(error);
                })
                .finally(() =>
                    context.commit(SET_LOADING_OPERAZIONE, false)
                );
        });
    },
};

const mutations = {
    [SET_PERIODI](state, list) {
        state.periodi = Array.isArray(list) ? list : [];
    },
    [SET_RIGHE](state, list) {
        state.righe = Array.isArray(list) ? list : [];
    },
    [SET_INVII](state, list) {
        state.invii = Array.isArray(list) ? list : [];
    },
    [SET_ERRORE_INVII](state, valore) {
        state.erroreInvii =
            typeof valore === "string" && valore !== "" ? valore : null;
    },
    [SET_LOADING](state, loading) {
        state.loading = loading;
    },
    [SET_LOADING_OPERAZIONE](state, loading) {
        state.loadingOperazione = loading;
    },
    [SET_ERROR](state, error) {
        state.error = error;
    },
    // Sempre null oppure stringa: distingue l'elenco vuoto dal caricamento fallito.
    [SET_ERRORE_CARICAMENTO](state, valore) {
        state.erroreCaricamento =
            typeof valore === "string" && valore !== "" ? valore : null;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
